import React, { forwardRef, type Ref } from "react";

import clsx from "clsx";
import { KBarResults, useMatches, type ActionImpl } from "kbar";
import { truncate } from "lodash";

import "./command-palette.scss";

export const CommandResults = () => {
  const { results } = useMatches();

  return (
    <KBarResults
      maxHeight={476}
      items={results}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <GroupLabel label={item} />
        ) : (
          <ResultItem active={active} item={item} />
        )
      }
    />
  );
};

const GroupLabel = forwardRef(
  ({ label }: { label: string }, ref: Ref<HTMLDivElement>) => {
    return (
      <div ref={ref} className={clsx("text-xxs text-gray py-2 px-4 uppercase")}>
        {label}
      </div>
    );
  },
);

const ResultItem = forwardRef(
  (
    { item, active }: { item: ActionImpl; active: boolean },
    ref: Ref<HTMLDivElement>,
  ) => {
    if (!item) {
      return null;
    }

    const isExternalLink = (item as any).isExternalLink;
    const apiMethod = (item as any).apiMethod;
    const isNew = (item as any).isNew;

    return (
      <div
        ref={ref}
        className={clsx(
          "command-palette-result-row",
          "text-sm cursor-pointer px-4 h-14 flex items-center justify-between text-v2-primary",
          {
            "bg-v2-secondary": active,
          },
        )}
      >
        <div className="flex items-center gap-3">
          {item.icon && (
            <div className="shrink-0 text-base w-[24px] flex items-center justify-center">
              {item.icon}
            </div>
          )}
          <div>
            <div className="flex items-center">
              {item.ancestors.length > 0 &&
                item.ancestors.map((ancestor) => (
                  <React.Fragment key={ancestor.id}>
                    <span className="text-gray+1">{ancestor.name}</span>
                    <span className="mx-2">&rsaquo;</span>
                  </React.Fragment>
                ))}
              <div className="flex items-center gap-2">
                {apiMethod && (
                  <div
                    className={clsx(
                      "text-xxxs text-white uppercase rounded py-[3px] px-[5px]",
                      {
                        "bg-alien+2": apiMethod === "get",
                        "bg-mars+2": apiMethod === "delete",
                        "bg-pluto+2": apiMethod === "post",
                        "bg-venus+2": apiMethod === "patch",
                      },
                    )}
                  >
                    {apiMethod}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  {isNew && (
                    <em
                      className={clsx(
                        "text-[9px] leading-3 text-white bg-pluto+2 py-[2px] px-2 rounded-full",
                        "not-italic",
                      )}
                    >
                      NEW
                    </em>
                  )}
                  {item.name}
                </div>
              </div>
            </div>
            {item.subtitle && (
              <div
                className="text-xxs text-cosmic command-palette-result-row__subtile"
                dangerouslySetInnerHTML={{
                  __html: truncate(item.subtitle, { length: 240 }),
                }}
              ></div>
            )}
          </div>
        </div>
        {isExternalLink && (
          <i
            className={clsx(
              "fa-regular fa-sharp fa-arrow-up-right-from-square text-xs text-gray+2 ml-4",
            )}
          ></i>
        )}
      </div>
    );
  },
);
